import { pipe } from "fp-ts/function"
import {
  map as Amap,
  last,
  compact as Acompact,
  intersperse as Aintersperse,
  reduce as Areduce,
} from "fp-ts/Array"
import {
  type NonEmptyArray,
  last as NEAlast,
  init as NEAinit,
} from "fp-ts/NonEmptyArray"
import {
  head as RNEAhead,
  last as RNEAlast,
  init as RNEAinit,
} from "fp-ts/ReadonlyNonEmptyArray"
import { map as RAmap, reduce as RAreduce } from "fp-ts/ReadonlyArray"
import { bindTo as IbindTo, let as Ilet } from "fp-ts/Identity"
import { slice, trimRight, split } from "fp-ts/string"
import { match } from "ts-pattern"

type PublicationItem = {
  publishDate: string
  title: string
  authors: Array<string>
  abstract: string
  link: string
  journal: string
  identifiers: Array<string>
  pubmedId: string
}

const formatFullAuthorList = (lastNames: NonEmptyArray<string>) =>
  pipe(
    lastNames,
    IbindTo("all"),
    Ilet("final", ({ all }) => NEAlast(all)),
    Ilet("rest", ({ all }) => NEAinit(all)),
    Ilet("withCommas", ({ rest }) =>
      pipe(
        rest,
        Aintersperse(", "),
        Areduce("", (b, a) => b + a),
      ),
    ),
    ({ withCommas, final }) => `${withCommas} & ${final}`,
  )

const shortenName = (name: string) =>
  pipe(
    name,
    IbindTo("full"),
    Ilet("parts", ({ full }) => pipe(full, split(" "))),
    Ilet("surname", ({ parts }) => RNEAlast(parts)),
    Ilet("given", ({ parts }) => RNEAinit(parts)),
    Ilet("firstInitials", ({ given }) =>
      pipe(
        given,
        RAmap((n) => pipe(n, split(""), RNEAhead)),
        RAreduce("", (b, a) => b + a),
      ),
    ),
    ({ firstInitials, surname }) => `${firstInitials} ${surname}`,
  )

const shortenAllNames = (names: Array<string>) => pipe(names, Amap(shortenName))

const getAuthorsCitationString = (
  authors: Array<string>,
  options?: { limit: number },
) => {
  const { limit } = { limit: 100, ...options }
  return pipe(
    authors,
    Amap((a) => a.split(" ")),
    Amap(last),
    Acompact,
    (lastNames) =>
      match(lastNames.length)
        .when(
          (l) => l === 1,
          () => lastNames[0],
        )
        .when(
          (l) => l === 2,
          () => `${lastNames[0]} & ${lastNames[1]}`,
        )
        .when(
          (l) => l > 2 && l <= limit,
          () => formatFullAuthorList(lastNames as NonEmptyArray<string>),
        )
        .when(
          (l) => l > limit,
          () => `${lastNames[0]} et al`,
        )
        .otherwise(() => `Unexpected Error`),
  )
}

const removeTags = (string: string) => string.replaceAll(/<\/?\w+>/g, "")

const getPublicationYear = (publicationDate: string) =>
  new Date(publicationDate).getFullYear()

const limitCharacters = (text: string, limit: number) =>
  text.length > limit ? `${text.slice(0, limit).trimEnd()}...` : text

// add check for already ending in punctation
const formatTitle = (title: string) =>
  pipe(
    title,
    IbindTo("initial"),
    Ilet("full", ({ initial }) => removeTags(initial)),
    Ilet("shortened", ({ full }) => pipe(full, slice(0, 90), trimRight)),
    Ilet("withEllipses", ({ shortened, full }) =>
      shortened === full ? `${shortened}.` : `${shortened}...`,
    ),
  )

const createCitation = ({
  authors,
  publishDate,
  title,
  journal,
}: PublicationItem) =>
  `${getAuthorsCitationString(authors)}. (${getPublicationYear(
    publishDate,
  )}). "${limitCharacters(removeTags(title), 90)}." ${journal}`

export {
  createCitation,
  getAuthorsCitationString,
  removeTags,
  getPublicationYear,
  limitCharacters,
  formatTitle,
  shortenName,
  shortenAllNames,
}
